import React, { useEffect, useState, useMemo } from 'react';
import { Form, ListGroup, InputGroup, Button } from 'react-bootstrap';
import { FiSearch, FiX, FiChevronDown } from 'react-icons/fi';
import { useWebProvider } from '../../hooks/defaults/useWebProvider';
import { endPointCaderno } from '../../models/constantes';

const RecursoSelector = ({ value, onSelect, className }) => {
    const { controller } = useWebProvider();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showList, setShowList] = useState(false);
    const [selectedResource, setSelectedResource] = useState(value);

    useEffect(() => {
        setSelectedResource(value);
    }, [value]);

    useEffect(() => {
        const fetchResources = async () => {
            setLoading(true);
            try {
                const data = await controller(null, endPointCaderno).get("recurso");
                if (Array.isArray(data)) {
                    setResources(data);
                }
            } catch (error) {
                console.error("Failed to load resources", error);
            } finally {
                setLoading(false);
            }
        };

        if (showList && resources.length === 0) {
            fetchResources();
        }
    }, [showList, controller, resources.length]);

    const groupedResources = useMemo(() => {
        const filtered = resources.filter(r => 
            r.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.agrupamento && r.agrupamento.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const groups = filtered.reduce((acc, curr) => {
            const group = curr.agrupamento || 'Sem Agrupamento';
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(curr);
            return acc;
        }, {});

        // Sort groups alphabetically
        return Object.keys(groups).sort().reduce((acc, key) => {
            acc[key] = groups[key];
            return acc;
        }, {});
    }, [resources, searchTerm]);

    const handleSelect = (resource) => {
        setSelectedResource(resource);
        onSelect(resource);
        setShowList(false);
        setSearchTerm('');
    };

    const clearSelection = (e) => {
        e.stopPropagation();
        setSelectedResource(null);
        onSelect(null);
        setSearchTerm('');
    };

    return (
        <div className={`recurso-selector ${className || ''}`} style={{ position: 'relative', marginBottom: '1rem' }}>
            <Form.Group>
                <Form.Label>Recurso</Form.Label>
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder="Selecione um recurso..."
                        value={selectedResource ? selectedResource.nome : searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            if (selectedResource) {
                                setSelectedResource(null);
                                onSelect(null);
                            }
                            setShowList(true);
                        }}
                        onFocus={() => setShowList(true)}
                        onClick={() => setShowList(true)}
                    />
                    {selectedResource ? (
                        <Button variant="outline-secondary" onClick={clearSelection}>
                            <FiX />
                        </Button>
                    ) : (
                        <Button variant="outline-secondary" onClick={() => setShowList(!showList)}>
                            {showList ? <FiX /> : <FiChevronDown />}
                        </Button>
                    )}
                </InputGroup>
                {selectedResource && selectedResource.agrupamento && (
                    <Form.Text className="text-muted">
                        Agrupamento: {selectedResource.agrupamento}
                    </Form.Text>
                )}
            </Form.Group>

            {showList && (
                <>
                    <div 
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} 
                        onClick={() => setShowList(false)}
                    />
                    <div className="recurso-list-container" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                        marginTop: '4px'
                    }}>
                        {loading ? (
                            <div className="p-3 text-center">Carregando...</div>
                        ) : (
                            Object.entries(groupedResources).map(([group, items]) => (
                                <div key={group}>
                                    <div className="bg-light p-2 font-weight-bold border-bottom border-top sticky-top" style={{position: 'sticky', top: 0, zIndex: 1}}>
                                        <small className="text-uppercase text-muted">{group}</small>
                                    </div>
                                    <ListGroup variant="flush">
                                        {items.map(item => (
                                            <ListGroup.Item 
                                                key={item.id} 
                                                action 
                                                onClick={() => handleSelect(item)}
                                                className="d-flex justify-content-between align-items-center"
                                                active={selectedResource && selectedResource.id === item.id}
                                            >
                                                {item.nome}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </div>
                            ))
                        )}
                        {!loading && Object.keys(groupedResources).length === 0 && (
                            <div className="p-3 text-center text-muted">Nenhum recurso encontrado</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default RecursoSelector;
